import ChannelSelector from "@components/ChannelSelector";
import ExportButton from "@components/ExportButton";
import { FilterBar, FilterRow } from "@components/FilterBar";
import GroupInfoBadge from "@components/GroupInfoBadge";
import ObjectSelect from "@components/ObjectSelect";
import PeriodFilter, { type PeriodRange } from "@components/PeriodFilter";
import { type ChannelMeterRow, useChannelsFlat, useObjects } from "@features/devices/api";
import { type DirectoryGroupChannel, useGroups } from "@features/groups/api";
import { PageLayoutWithHelp } from "@features/page-help";
import {
  type AggregatedProfilePoint,
  type ProfileRow,
  useProfileData,
  useProfileGroupData,
} from "@features/reports/api";
import { buildExportUrl } from "@shared/buildExportUrl";
import { buildTrend } from "@shared/buildTrend";
import { useGlobalObject } from "@shared/hooks/useGlobalObject";
import ContentSpin from "@shared/ui/content-spin";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { useDefaultEmail } from "@shared/useDefaultEmail";
import { useMailComposer } from "@shared/useMailComposer";
import { useStandardPagination } from "@shared/useStandardPagination";
import { Alert, Radio, Segmented, Select, Space, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import ProfileChart from "./ProfileChart";

type ChannelMode = "channel" | "group";
type ViewMode = "chart" | "table";

const measurementOptions = [
  { label: "A+", value: 1 },
  { label: "A-", value: 2 },
  { label: "R+", value: 3 },
  { label: "R-", value: 4 },
];

const stepOptions = [
  { label: "30 мин", value: 30 },
  { label: "60 мин", value: 60 },
  { label: "1 день", value: 1440 },
];

const modeOptions = [
  { label: "Энергия", value: "energy" },
  { label: "Мощность", value: "power" },
];

const formatNumber = (value?: number, digits = 3) => {
  if (value === undefined || value === null) {
    return "—";
  }
  return value.toFixed(digits);
};

const ReportsProfileDataPage = () => {
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];
  const [viewMode, setViewMode] = useState<ViewMode>("chart");
  const { selectedObject, setSelectedObject } = useGlobalObject(objects);
  const [channelMode, setChannelMode] = useState<ChannelMode>("channel");
  const [selectedChannel, setSelectedChannel] = useState<number>();
  const [selectedGroup, setSelectedGroup] = useState<string>();
  const [selectedGroupChannel, setSelectedGroupChannel] = useState<number | null>(null);
  const [measurement, setMeasurement] = useState<number>(1);
  const [dataMode, setDataMode] = useState<string>("energy");
  const [step, setStep] = useState<number>(30);
  const [period, setPeriod] = useState<PeriodRange>(() => {
    const start = dayjs().startOf("day");
    return { from: start, to: start.add(1, "day") };
  });

  const regionID = selectedObject?.regionId;
  const objectID = selectedObject?.objectId;

  // Загрузка каналов объекта
  const channelsQuery = useChannelsFlat(objectID, undefined, 500);
  const channels = channelsQuery.data ?? [];
  const channelOptions = useMemo(() => {
    return channels.map((channel: ChannelMeterRow) => ({
      label: `${channel.meter_number} — ${channel.feeder_name}`,
      value: channel.meter_number,
      description: channel.channel_label,
    }));
  }, [channels]);

  // Загрузка групп объекта (публичный доступ для всех пользователей)
  const groupsQuery = useGroups(
    selectedObject
      ? { object: { regionId: selectedObject.regionId, objectId: selectedObject.objectId } }
      : undefined,
  );
  const groups = groupsQuery.data ?? [];

  const groupOptions = useMemo(() => {
    if (!groups.length) return [];
    return groups.map((g) => ({
      label: `${g.name} (${g.channels?.length ?? 0} каналов)`,
      value: g.id,
    }));
  }, [groups]);

  // Каналы выбранной группы
  const selectedGroupData = useMemo(() => {
    if (!selectedGroup || !groups.length) return null;
    return groups.find((g) => g.id === selectedGroup) ?? null;
  }, [groups, selectedGroup]);

  const groupChannels: DirectoryGroupChannel[] = selectedGroupData?.channels ?? [];

  const groupChannelOptions = useMemo(() => {
    if (!groupChannels.length) return [];
    return groupChannels.map((ch) => {
      const channelInfo = channels.find((c) => c.meter_number === ch.channel_id);
      const label = channelInfo
        ? `${ch.channel_id} — ${channelInfo.feeder_name ?? channelInfo.channel_label ?? "канал"}`
        : `Канал ${ch.channel_id}`;
      return { label, value: ch.channel_id };
    });
  }, [groupChannels, channels]);

  // Автовыбор канала в режиме "Канал"
  useEffect(() => {
    if (channelMode !== "channel") return;
    if (selectedChannel && channelOptions.some((option) => option.value === selectedChannel)) {
      return;
    }
    if (channelOptions.length > 0) {
      setSelectedChannel(channelOptions[0].value);
    } else {
      setSelectedChannel(undefined);
    }
  }, [channelOptions, selectedChannel, channelMode]);

  // Автовыбор первой группы в режиме "Группа"
  useEffect(() => {
    if (channelMode === "group" && !selectedGroup && groups.length > 0) {
      setSelectedGroup(groups[0].id);
    }
  }, [groups, selectedGroup, channelMode]);

  // Сброс канала группы при смене группы
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional reset on group change
  useEffect(() => {
    setSelectedGroupChannel(null);
  }, [selectedGroup]);

  // Режим группового агрегирования: группа выбрана, но канал не выбран
  const isGroupAggregateMode = channelMode === "group" && !!selectedGroup && !selectedGroupChannel;

  // Эффективный канал для запроса (только для индивидуального режима)
  const effectiveChannel =
    channelMode === "channel" ? selectedChannel : (selectedGroupChannel ?? undefined);

  const fromISO = period.from?.format("YYYY-MM-DD");
  const toISO = period.to?.format("YYYY-MM-DD");

  // Запрос для индивидуального канала
  const profileQuery = useProfileData({
    region: regionID,
    object: objectID,
    channel: effectiveChannel,
    from: fromISO,
    to: toISO,
    measurement,
    step,
    mode: dataMode,
  });

  // Запрос для группового агрегирования
  const groupProfileQuery = useProfileGroupData({
    groupID: isGroupAggregateMode ? selectedGroup : undefined,
    from: fromISO,
    to: toISO,
    measurement,
    step,
    mode: dataMode,
  });

  // Унифицированный тип строки для таблицы
  type UnifiedRow = {
    period_start: string;
    period_end: string;
    value?: number | null;
    value_with_losses?: number | null;
    status?: number | null;
    channels_count?: number | null;
    measurement_label?: string | null;
    interval_minutes?: number | null;
    samples_actual?: number | null;
    samples_expected?: number | null;
    has_bypass?: boolean;
    events_count?: number | null;
  };

  // Преобразование данных группы в унифицированный формат
  const groupData: UnifiedRow[] = useMemo(() => {
    if (!groupProfileQuery.data?.data) return [];
    return groupProfileQuery.data.data.map((point: AggregatedProfilePoint) => ({
      period_start: point.period_start,
      period_end: point.period_end,
      value: point.sum,
      value_with_losses: point.sum_with_losses,
      status: point.max_status,
      channels_count: point.channels_count,
      measurement_label: measurementOptions.find((o) => o.value === measurement)?.label,
    }));
  }, [groupProfileQuery.data, measurement]);

  // Преобразование данных группы в формат ProfileRow для графика (с фронтенд-трендом)
  const groupChartRows: ProfileRow[] = useMemo(() => {
    if (!groupProfileQuery.data?.data) return [];
    const rawPoints = groupProfileQuery.data.data;

    // Вычисляем тренд на фронтенде
    const trendPoints = rawPoints
      .map((p) => ({ x: dayjs(p.period_start).valueOf(), y: p.sum }))
      .filter((p) => typeof p.y === "number" && Number.isFinite(p.y));

    const trend = buildTrend(trendPoints);

    return rawPoints.map((point: AggregatedProfilePoint) => {
      const x = dayjs(point.period_start).valueOf();
      const trendVal = trend ? trend.intercept + trend.slope * x : undefined;
      const trendUpper = trend && trendVal != null ? trendVal + trend.stdDev : undefined;
      const trendLower = trend && trendVal != null ? trendVal - trend.stdDev : undefined;

      return {
        region_id: 0,
        object_id: 0,
        object_name: "",
        feeder_id: 0,
        feeder_name: "",
        meter_number: 0,
        channel_label: "",
        measurement_type: measurement,
        measurement_label: measurementOptions.find((o) => o.value === measurement)?.label ?? "",
        period_start: point.period_start,
        period_end: point.period_end,
        interval_minutes: step,
        mode: dataMode,
        value: point.sum,
        value_with_losses: point.sum_with_losses,
        status: point.max_status,
        has_bypass: false,
        trend_value: trendVal,
        trend_upper_value: trendUpper,
        trend_lower_value: trendLower,
      };
    });
  }, [groupProfileQuery.data, measurement, step, dataMode]);

  // Преобразование данных индивидуального канала
  const channelData: UnifiedRow[] = useMemo(() => {
    if (!profileQuery.data) return [];
    return profileQuery.data.map((row: ProfileRow) => ({
      period_start: row.period_start,
      period_end: row.period_end,
      value: row.value,
      value_with_losses: row.value_with_losses,
      status: row.status,
      measurement_label: row.measurement_label,
      interval_minutes: row.interval_minutes,
      samples_actual: row.samples_actual,
      samples_expected: row.samples_expected,
      has_bypass: row.has_bypass,
      events_count: row.events_count,
    }));
  }, [profileQuery.data]);

  // Выбор данных в зависимости от режима
  const data = isGroupAggregateMode ? groupData : channelData;
  const isLoading = isGroupAggregateMode ? groupProfileQuery.isLoading : profileQuery.isLoading;
  const isFetching = isGroupAggregateMode ? groupProfileQuery.isFetching : profileQuery.isFetching;
  const refetch = isGroupAggregateMode ? groupProfileQuery.refetch : profileQuery.refetch;

  const pagination = useStandardPagination(data.length);
  const totals = useMemo(
    () =>
      data.reduce(
        (acc, row) => {
          if (row.value != null) {
            acc.value += row.value;
          }
          if (row.value_with_losses != null) {
            acc.losses += row.value_with_losses;
          }
          return acc;
        },
        { value: 0, losses: 0 },
      ),
    [data],
  );

  // Метаданные группы для отображения
  const groupMeta = groupProfileQuery.data?.meta;

  const columns: ColumnsType<UnifiedRow> = [
    {
      title: "Период",
      dataIndex: "period_start",
      width: 220,
      render: (_value, row) =>
        `${dayjs(row.period_start).format("DD.MM.YYYY HH:mm")} → ${dayjs(row.period_end).format("HH:mm")}`,
    },
    {
      title: "Измерение",
      dataIndex: "measurement_label",
      width: 100,
    },
    {
      title: dataMode === "power" ? "Мощность" : "Энергия",
      dataIndex: "value",
      width: 140,
      render: (value?: number) => formatNumber(value),
    },
    {
      title: dataMode === "power" ? "Мощность (потери)" : "Энергия (потери)",
      dataIndex: "value_with_losses",
      width: 160,
      render: (value?: number) => formatNumber(value),
    },
    {
      title: "Статус",
      dataIndex: "status",
      width: 120,
      render: (value?: number) => {
        if (value === undefined || value === null) {
          return "—";
        }
        return <Tag color={value === 0 ? "green" : "red"}>{value}</Tag>;
      },
    },
    ...(isGroupAggregateMode
      ? [
          {
            title: "Каналов",
            dataIndex: "channels_count",
            width: 100,
            render: (value?: number) => value ?? "—",
          },
        ]
      : [
          {
            title: "Интервалы",
            key: "intervals",
            width: 140,
            render: (_value: unknown, row: UnifiedRow) =>
              `${row.samples_actual ?? 0} / ${row.samples_expected ?? (row.interval_minutes ?? 30) / 30}`,
          },
          {
            title: "Обходные",
            dataIndex: "has_bypass",
            width: 120,
            render: (value?: boolean) => (value ? <Tag color="blue">Есть</Tag> : "—"),
          },
          {
            title: "События",
            dataIndex: "events_count",
            width: 120,
            render: (value?: number) => value ?? 0,
          },
        ]),
  ];

  // Готовность к запросу: либо групповой режим с группой, либо индивидуальный с каналом
  const ready = isGroupAggregateMode
    ? !!selectedGroup && !!fromISO && !!toISO
    : regionID !== undefined &&
      objectID !== undefined &&
      effectiveChannel !== undefined &&
      !!fromISO &&
      !!toISO;

  const exportUrl = ready
    ? isGroupAggregateMode
      ? buildExportUrl(`/api/reports/groups/${selectedGroup}/profile`, {
          from: fromISO,
          to: toISO,
          measurement,
          step,
          mode: dataMode,
        })
      : buildExportUrl("/api/reports/profile", {
          region: regionID,
          object: objectID,
          channel: effectiveChannel,
          from: fromISO,
          to: toISO,
          measurement,
          step,
          mode: dataMode,
        })
    : undefined;
  const currentObject = useMemo(
    () => objects.find((obj) => obj.n_ob === objectID && obj.syb_rnk === regionID),
    [objects, objectID, regionID],
  );
  const channelLabel = useMemo(() => {
    if (isGroupAggregateMode) {
      const groupName = groupMeta?.name ?? selectedGroupData?.name ?? "Группа";
      const channelsCount = groupMeta?.channels_total ?? selectedGroupData?.channels?.length ?? 0;
      return `${groupName} (${channelsCount} каналов)`;
    }
    if (channelMode === "group" && selectedGroupChannel) {
      const opt = groupChannelOptions.find((o) => o.value === selectedGroupChannel);
      return opt?.label ?? `Счётчик №${selectedGroupChannel}`;
    }
    const opt = channelOptions.find((option) => option.value === effectiveChannel);
    return opt?.label ?? (effectiveChannel ? `Счётчик №${effectiveChannel}` : "—");
  }, [
    isGroupAggregateMode,
    groupMeta,
    selectedGroupData,
    channelMode,
    selectedGroupChannel,
    groupChannelOptions,
    channelOptions,
    effectiveChannel,
  ]);
  const defaultEmail = useDefaultEmail();
  const { openComposer, mailModal, sendDisabled } = useMailComposer({ defaultEmail, exportUrl });
  const measurementLabel =
    measurementOptions.find((option) => option.value === measurement)?.label ?? `№${measurement}`;
  const stepLabel = stepOptions.find((option) => option.value === step)?.label ?? `${step} мин`;
  const dataModeLabel = modeOptions.find((option) => option.value === dataMode)?.label ?? dataMode;
  const periodLabel = `${dayjs(fromISO).format("DD.MM.YYYY HH:mm")} — ${dayjs(toISO).format("DD.MM.YYYY HH:mm")}`;

  const handleSendMail = useCallback(() => {
    if (!ready) {
      return;
    }
    const objectLabel = currentObject?.txt
      ? `${currentObject.txt} (№${currentObject.n_ob})`
      : objectID
        ? `Объект №${objectID}`
        : "Объект";
    openComposer({
      subject: `Данные профиля — ${objectLabel}`,
      body: `Отчёт: Профильные данные\nОбъект: ${objectLabel}\nСчётчик: ${channelLabel}\nПериод: ${periodLabel}\nПоказатель: ${measurementLabel}\nРежим: ${dataModeLabel}\nИнтервал: ${stepLabel}\n\nВо вложении прикреплён XLSX.`,
      filename: `profile_${effectiveChannel ?? "channel"}_${dayjs(fromISO).format("YYYYMMDD")}.xlsx`,
    });
  }, [
    ready,
    currentObject,
    objectID,
    channelLabel,
    periodLabel,
    measurementLabel,
    dataModeLabel,
    stepLabel,
    openComposer,
    effectiveChannel,
    fromISO,
  ]);

  return (
    <PageLayoutWithHelp
      title="Данные профиля (интервалы)"
      extra={<ExportButton url={exportUrl} onSend={handleSendMail} sendDisabled={sendDisabled} />}
    >
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <FilterBar>
          <FilterRow>
            <Segmented
              value={channelMode}
              onChange={(value) => {
                setChannelMode(value as ChannelMode);
                setSelectedChannel(undefined);
                setSelectedGroup(undefined);
                setSelectedGroupChannel(null);
              }}
              options={[
                { label: "Канал", value: "channel" },
                { label: "Группа", value: "group" },
              ]}
            />
            <ObjectSelect
              objects={objects}
              loading={objectsQuery.isLoading}
              value={selectedObject}
              onChange={(value) => {
                setSelectedObject(value);
                setSelectedChannel(undefined);
                setSelectedGroup(undefined);
                setSelectedGroupChannel(null);
              }}
              style={{ minWidth: 280, maxWidth: 400 }}
            />
            {channelMode === "channel" ? (
              <ChannelSelector
                options={channelOptions}
                value={selectedChannel}
                loading={channelsQuery.isLoading}
                onChange={(value) => setSelectedChannel(value)}
              />
            ) : (
              <>
                <Select
                  showSearch
                  placeholder="Группа каналов"
                  disabled={!selectedObject}
                  loading={groupsQuery.isLoading}
                  options={groupOptions}
                  value={selectedGroup}
                  onChange={setSelectedGroup}
                  optionFilterProp="label"
                  style={{ minWidth: 240, maxWidth: 400 }}
                  notFoundContent={
                    groupsQuery.isLoading ? "Загрузка..." : "Нет групп для этого объекта"
                  }
                />
                <Select
                  showSearch
                  allowClear
                  placeholder="Канал группы"
                  disabled={!selectedGroup || groupChannelOptions.length === 0}
                  options={groupChannelOptions}
                  value={selectedGroupChannel}
                  onChange={setSelectedGroupChannel}
                  optionFilterProp="label"
                  style={{ minWidth: 200, maxWidth: 320 }}
                  notFoundContent="Нет каналов в группе"
                />
              </>
            )}
          </FilterRow>
          <FilterRow>
            <Select
              style={{ width: 140 }}
              options={measurementOptions}
              value={measurement}
              onChange={(value) => setMeasurement(value)}
            />
            <Select
              style={{ width: 160 }}
              options={stepOptions}
              value={step}
              onChange={(value) => setStep(value)}
            />
            <Radio.Group
              options={modeOptions}
              value={dataMode}
              onChange={(e) => setDataMode(e.target.value)}
              optionType="button"
              buttonStyle="solid"
            />
          </FilterRow>
          <div style={{ width: "100%" }}>
            <PeriodFilter value={period} onChange={setPeriod} showMonthControls={false} />
          </div>
        </FilterBar>
        {!ready && (
          <Alert
            type="info"
            showIcon
            title={
              channelMode === "channel"
                ? "Выберите объект, канал и дату"
                : "Выберите группу для просмотра данных"
            }
            description={
              channelMode === "channel"
                ? "Для отображения профильных данных необходимо выбрать все параметры фильтров."
                : "Выберите группу каналов для просмотра агрегированных данных. Можно детализировать по конкретному каналу."
            }
            style={{ maxWidth: 600 }}
          />
        )}
        {ready && isGroupAggregateMode && groupMeta && (
          <GroupInfoBadge
            name={groupMeta.name}
            channelsProcessed={groupMeta.channels_processed}
            channelsTotal={groupMeta.channels_total}
            warning={groupMeta.warning ?? undefined}
          />
        )}
        {ready && (
          <Segmented
            value={viewMode}
            onChange={(v) => setViewMode(v as ViewMode)}
            options={[
              { label: "График", value: "chart" },
              { label: "Таблица", value: "table" },
            ]}
            style={{ marginBottom: 16 }}
          />
        )}
        {ready && (
          <ContentSpin spinning={isFetching}>
            {viewMode === "chart" && !isGroupAggregateMode && profileQuery.data && (
              <ProfileChart rows={profileQuery.data} dataMode={dataMode} showLosses />
            )}
            {viewMode === "chart" && isGroupAggregateMode && groupChartRows.length > 0 && (
              <ProfileChart rows={groupChartRows} dataMode={dataMode} showLosses />
            )}
            {viewMode === "chart" && !isFetching && data.length === 0 && (
              <div style={{ minHeight: 200 }} />
            )}
            {viewMode === "table" && (
              <EnterpriseTable<UnifiedRow>
                rowKey={(row) => `${row.period_start}-${row.period_end}`}
                columns={columns}
                dataSource={data}
                loading={isLoading}
                pagination={pagination}
                scroll={{ x: 1200 }}
                size="small"
                toolbar={{ reload: true, onReload: () => refetch() }}
                summary={() => (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={2}>
                        <Typography.Text strong>Всего:</Typography.Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2}>
                        <Typography.Text strong>{formatNumber(totals.value)}</Typography.Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3}>
                        <Typography.Text strong>{formatNumber(totals.losses)}</Typography.Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} colSpan={isGroupAggregateMode ? 2 : 4}>
                        &nbsp;
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
            )}
          </ContentSpin>
        )}
        {mailModal}
      </Space>
    </PageLayoutWithHelp>
  );
};

export default ReportsProfileDataPage;
